import React, { useState, useEffect, useRef } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import {
  AppBar, Box, Hidden, IconButton, Toolbar, makeStyles,
  SvgIcon, Typography, Badge,
} from '@material-ui/core'
import {
  Menu as MenuIcon,
  Calendar as CalendarIcon,
  Users as UsersIcon,
  ShoppingBag as SalesIcon,
  DollarSign as IncomeIcon,
  BarChart2 as ReportsIcon,
  Package as ProductsIcon,
  Grid as AppsIcon,
  Bell as BellIcon,
  MessageSquare as InboxIcon,
} from 'react-feather'
import { useTranslation } from 'react-i18next'
import Logo from 'src/components/Logo'
import Account from './Account'
import LanguageSwitcher from 'src/components/LanguageSwitcher'
import FontSizeSwitcher from 'src/components/FontSizeSwitcher'
import AppsLauncher from './AppsLauncher'
import conversationApi from 'src/api/conversation'

const NAV_ITEMS = [
  { key: 'appointment', href: '/app/appointment',         icon: CalendarIcon },
  { key: 'customers',   href: '/app/management/customers',icon: UsersIcon   },
  { key: 'orders',      href: '/app/management/orders',   icon: SalesIcon   },
  { key: 'income',      href: '/app/finance/income',      icon: IncomeIcon  },
  { key: 'summary',     href: '/app/finance/summary',     icon: ReportsIcon },
  { key: 'expense',     href: '/app/finance/expense',     icon: ProductsIcon},
]

const useStyles = makeStyles(() => ({
  root: {
    zIndex: 1300,
    boxShadow: 'none',
    backgroundColor: '#1a1a2e',
  },
  toolbar: {
    minHeight: 56,
    padding: '0 16px',
    gap: 4,
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 8,
    flexShrink: 0,
    textDecoration: 'none',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.75)',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      background: 'rgba(255,255,255,0.08)',
      color: '#fff',
    },
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
  },
  appsBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.75)',
    fontSize: 13,
    fontWeight: 500,
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      background: 'rgba(255,255,255,0.08)',
      color: '#fff',
    },
  },
  rightIcon: {
    color: 'rgba(255,255,255,0.75)',
    padding: 8,
    '&:hover': { background: 'rgba(255,255,255,0.08)', color: '#fff' },
  },
  dividerV: {
    width: 1,
    height: 24,
    background: 'rgba(255,255,255,0.15)',
    margin: '0 8px',
    flexShrink: 0,
  },
}))

const TopBar = ({ className, onMobileNavOpen, ...rest }) => {
  const classes = useStyles()
  const location = useLocation()
  const { t } = useTranslation()
  const [appsOpen, setAppsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const sseRef      = useRef(null)
  const retryTimer  = useRef(null)

  // ── Init: fetch actual unread count once on mount ──────────────────────────
  useEffect(() => {
    if (window.location.pathname.startsWith('/app/inbox')) return
    conversationApi.getInboxList({ status: 'open' })
      .then((data) => {
        // guard: user may have navigated to inbox while request was in-flight
        if (window.location.pathname.startsWith('/app/inbox')) return
        const count = (data || []).filter((c) => (c.unreadCount || 0) > 0).length
        setUnreadCount(count)
      })
      .catch(() => {})
  }, [])

  // ── SSE: increment badge when new message arrives, retry on error ──────────
  useEffect(() => {
    let destroyed = false

    const connect = () => {
      const token = localStorage.getItem('accessToken')
      if (!token || destroyed) return

      if (sseRef.current) { sseRef.current.close(); sseRef.current = null }

      sseRef.current = conversationApi.subscribeToInboxStream({
        onMessage: () => {
          if (!window.location.pathname.startsWith('/app/inbox')) {
            setUnreadCount((n) => n + 1)
          }
        },
        onError: () => {
          if (sseRef.current) { sseRef.current.close(); sseRef.current = null }
          if (!destroyed) {
            retryTimer.current = setTimeout(connect, 15000)
          }
        },
      })
    }

    connect()

    return () => {
      destroyed = true
      if (retryTimer.current) clearTimeout(retryTimer.current)
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null }
    }
  }, [])

  // Sync badge with actual unread count on every route change
  useEffect(() => {
    if (location.pathname.startsWith('/app/inbox')) {
      // On inbox page: clear badge immediately
      setUnreadCount(0)
    } else {
      // Leaving inbox (or any other navigation): re-fetch real unread count
      conversationApi.getInboxList({ status: 'open' })
        .then((data) => {
          if (window.location.pathname.startsWith('/app/inbox')) return
          const count = (data || []).filter((c) => (c.unreadCount || 0) > 0).length
          setUnreadCount(count)
        })
        .catch(() => {})
    }
  }, [location.pathname])

  return (
    <>
      <AppBar className={classes.root} {...rest}>
        <Toolbar className={classes.toolbar}>
          {/* Mobile hamburger */}
          <Hidden lgUp>
            <IconButton className={classes.rightIcon} onClick={onMobileNavOpen} size="small">
              <SvgIcon fontSize="small"><MenuIcon /></SvgIcon>
            </IconButton>
          </Hidden>

          {/* Logo */}
          <RouterLink to="/" className={classes.logoWrap}>
            <Logo />
          </RouterLink>

          {/* Desktop nav items */}
          <Hidden mdDown>
            <Box display="flex" alignItems="center" gap={2} ml={1}>
              {NAV_ITEMS.map((item) => {
                const active = location.pathname.startsWith(item.href)
                return (
                  <RouterLink
                    key={item.href}
                    to={item.href}
                    className={`${classes.navItem} ${active ? classes.navItemActive : ''}`}
                  >
                    <SvgIcon style={{ fontSize: 15 }}><item.icon /></SvgIcon>
                    <span>{t(`nav.${item.key}`)}</span>
                  </RouterLink>
                )
              })}
            </Box>
          </Hidden>

          <Box flexGrow={1} />

          {/* Desktop right section */}
          <Hidden mdDown>
            {/* Apps launcher */}
            <button className={classes.appsBtn} onClick={() => setAppsOpen(true)}>
              <SvgIcon style={{ fontSize: 16 }}><AppsIcon /></SvgIcon>
              <span>Apps</span>
            </button>

            <Box className={classes.dividerV} />

            {/* Notifications */}
            <IconButton className={classes.rightIcon} size="small">
              <Badge badgeContent={0} color="error">
                <SvgIcon style={{ fontSize: 18 }}><BellIcon /></SvgIcon>
              </Badge>
            </IconButton>

            <Box className={classes.dividerV} />

            {/* Inbox — SMS conversations */}
            <RouterLink
              to="/app/inbox"
              className={`${classes.navItem} ${location.pathname.startsWith('/app/inbox') ? classes.navItemActive : ''}`}
              style={{ textDecoration: 'none' }}
            >
              <Badge badgeContent={unreadCount} color="error" max={99}>
                <SvgIcon style={{ fontSize: 16 }}><InboxIcon /></SvgIcon>
              </Badge>
              <span style={{ marginLeft: 6 }}>Inbox</span>
            </RouterLink>

            <Box className={classes.dividerV} />
          </Hidden>

          {/* Language + Font size + Account */}
          <LanguageSwitcher color="#fff" />
          <FontSizeSwitcher iconColor="rgba(255,255,255,0.75)" />
          <Box ml={1}>
            <Account />
          </Box>
        </Toolbar>
      </AppBar>

      <AppsLauncher open={appsOpen} onClose={() => setAppsOpen(false)} />
    </>
  )
}

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func,
}

TopBar.defaultProps = {
  onMobileNavOpen: () => {},
}

export default TopBar
